import React, { useMemo } from 'react';
import {
  CreditPlanTypes,
  isCancelledSubscription,
  SemanticInformationType,
  SubscriptionStates,
  V2Plan,
} from '../../utilities/types';
import { formatCurrency } from '../../../../lib/currency';
import { PaymentPlanBlock } from '../../molecules/registration/PaymentPlanBlock';
import { styles } from './styles';
import { colorGunmetal, rebrand } from '../../../../theme/color';
import { StarCircle } from '../../atoms/icons/StarCircle';
import { MoneyBackGuarantee, TrialPlanTooltip } from './Plans';
import { classes } from 'typestyle';
import { toDateString } from '../../utilities/date';
import { ActionButton } from '../../atoms/button/ActionButton';
import { Checkmark } from '../../atoms/icons/Checkmark';
import { Button } from '../../atoms/button/Button';
import { ButtonProminence, ButtonSize } from '../../atoms/button/buttonStyles';
import { PlanFlag } from '../../atoms/PlanFlag';
import { AccessTime } from '../../atoms/icons/AccessTime';
import { Refresh } from '../../atoms/icons/Refresh';
import { PlanFeatures } from '../../molecules/plans-v2/PlanFeatures';
import { differenceInCalendarDays } from 'date-fns';
import { height } from 'csstips';
import {
  ActiveSubscriptionSchema,
  PendingSubscriptionSchema,
  SubscriptionDiscountSchema,
} from '../../../../types/schema';

interface PlanCardProps {
  plan: V2Plan;
  allPlans?: V2Plan[];
  planLoadingState?: boolean;
  isRegistration?: boolean;
  isSubscribedToLegacyPlan?: boolean;
  currentSubscription?: ActiveSubscriptionSchema;
  pendingSubscription?: PendingSubscriptionSchema;
  subscriptionDiscount?: SubscriptionDiscountSchema;
  handleUpgrade: (plan: V2Plan) => void;
  handleDowngrade: (plan: V2Plan) => void;
  handleSelectPlan: (plan: V2Plan) => void;
  handleResumeCancelledPlan: (plan: V2Plan) => void;
}

enum PlanActions {
  PLAN_ACTIVE = 'planActive',
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
  SELECT_PLAN = 'selectPlan',
  RESUBSCRIBE = 'resubscribe',
}

enum PlanFlagTypes {
  BACKSTAGE = 'backstage',
  DISCOUNTED = 'discounted',
  CANCELLED_PLAN = 'cancelledPlan',
  ACTIVE_TRIAL = 'activeTrial',
  TRIAL_OFFERED = 'trialOffered',
}

export const PlanCard = ({
  plan,
  allPlans = [],
  planLoadingState = null,
  isRegistration = false,
  isSubscribedToLegacyPlan = false,
  currentSubscription = null,
  pendingSubscription = null,
  subscriptionDiscount = null,
  handleUpgrade,
  handleDowngrade,
  handleSelectPlan,
  handleResumeCancelledPlan,
}: PlanCardProps) => {
  const isSubscribedToV2Plan = currentSubscription !== null;
  const isSubscribedToV2PlanAndCancelled = isSubscribedToV2Plan && isCancelledSubscription(currentSubscription.status);
  const isSubscribedToV2TrialPlan =
    isSubscribedToV2Plan &&
    [SubscriptionStates.TRIAL, SubscriptionStates.CANCELLED_TRIAL].includes(currentSubscription.status);

  // Only v2 plans users can upgrade/downgrade to other v2 plans
  // Users can only upgrade/downgrade to paid plans, not Backstage plans [WEB-7809]
  const availableUpgradePlans =
    currentSubscription && !isSubscribedToV2PlanAndCancelled
      ? allPlans.filter((plan) => plan.price > 0 && plan.price > currentSubscription.price)
      : [];
  const availableDowngradePlans =
    currentSubscription && !isSubscribedToV2PlanAndCancelled
      ? allPlans.filter((plan) => plan.price > 0 && plan.price < currentSubscription.price)
      : [];

  const canUpgrade =
    availableUpgradePlans.length &&
    availableUpgradePlans.find((upgradePlan) => upgradePlan.plan_name === plan.plan_name)
      ? true
      : false;
  const canDowngrade =
    availableDowngradePlans.length &&
    availableDowngradePlans.find((downgradePlan) => downgradePlan.plan_name === plan.plan_name)
      ? true
      : false;

  const isActivePlan = currentSubscription && currentSubscription.plan_name === plan.plan_name;
  const isPendingPlan = pendingSubscription && pendingSubscription.plan_name === plan.plan_name;
  const isBackstagePlan = plan.plan_name === CreditPlanTypes.BACKSTAGE;
  const isLaunchpadPlan = plan.plan_name === CreditPlanTypes.LAUNCHPAD;
  const isTrialPlan = plan.is_trial;
  const isActiveAndDiscountedPlan = isActivePlan && subscriptionDiscount;

  // Format the plan price and split it into units and remainder for display purposes
  const [units, remainder] = formatCurrency(plan.price / 100, plan.currency_code).split('.');

  const showDiscountedPrice =
    !!plan.price && !!plan.discount && plan.discounted_price !== null && (isRegistration || isActiveAndDiscountedPlan);
  let discountedUnits = null;
  let discountedRemainder = null;
  let discountDuration = null;

  if (showDiscountedPrice) {
    [discountedUnits, discountedRemainder] = formatCurrency(plan.discounted_price / 100, plan.currency_code).split('.');
    discountDuration = plan.discount.duration_in_months === 1 ? 'month' : `${plan.discount.duration_in_months} months`;
  }

  const showMoneyBackGuarantee = !isSubscribedToV2Plan && !isBackstagePlan && !isTrialPlan;
  const creditsIconColour = isTrialPlan
    ? colorGunmetal.toString()
    : isLaunchpadPlan
    ? rebrand.neutralOnDark[70].toString()
    : rebrand.primary[100].toString();

  const daysUntilRenewal = currentSubscription
    ? differenceInCalendarDays(new Date(currentSubscription.renewal_date), Date.now())
    : null;

  let creditsText = null;

  if (
    isSubscribedToV2Plan &&
    !isSubscribedToV2PlanAndCancelled &&
    !pendingSubscription &&
    !isActivePlan &&
    !isBackstagePlan
  ) {
    const currentCredits = currentSubscription ? currentSubscription.plan_credits : 0;
    const planCredits = plan.plan_credits;

    if (planCredits > currentCredits) {
      const extraCredits = planCredits - currentCredits;
      creditsText = `You'll get ${extraCredits} more credit${extraCredits !== 1 ? 's' : ''} each month`;
    } else if (currentCredits > planCredits) {
      const fewerCredits = currentCredits - planCredits;
      creditsText = `You'll get ${fewerCredits} ${fewerCredits === 1 ? 'less credit' : 'fewer credits'} each month`;
    }
  }

  // Determines which banner, if any, to apply at the top of the plan
  const planBanner = useMemo((): PlanFlagTypes => {
    if (isBackstagePlan && !isSubscribedToV2PlanAndCancelled) {
      return PlanFlagTypes.BACKSTAGE;
    }

    if (showDiscountedPrice && !isSubscribedToV2PlanAndCancelled) {
      return PlanFlagTypes.DISCOUNTED;
    }

    if (isTrialPlan && !isActivePlan && !isSubscribedToV2PlanAndCancelled) {
      return PlanFlagTypes.TRIAL_OFFERED;
    }

    if (isActivePlan) {
      if (isSubscribedToV2TrialPlan) {
        return PlanFlagTypes.ACTIVE_TRIAL;
      }

      if (isSubscribedToV2PlanAndCancelled) {
        return PlanFlagTypes.CANCELLED_PLAN;
      }
    }

    return null;
  }, []);

  // Determines which CTA button to display
  const planAction = useMemo((): PlanActions => {
    if (isSubscribedToV2PlanAndCancelled) {
      return isActivePlan ? PlanActions.RESUBSCRIBE : PlanActions.SELECT_PLAN;
    }

    if (isActivePlan) {
      return PlanActions.PLAN_ACTIVE;
    }
    if (canUpgrade) {
      return PlanActions.UPGRADE;
    }
    if (canDowngrade) {
      return PlanActions.DOWNGRADE;
    }
    if (!isSubscribedToV2Plan || isSubscribedToLegacyPlan) {
      return PlanActions.SELECT_PLAN;
    }

    return null;
  }, []);

  return (
    <PaymentPlanBlock
      className={classes(
        styles.planWrapper,
        isBackstagePlan && 'plan-backstage',
        isTrialPlan && 'plan-trial',
        isLaunchpadPlan && 'plan-launchpad'
      )}
      data-test-plan-block={plan.plan_name}
    >
      {planBanner === PlanFlagTypes.CANCELLED_PLAN && (
        <PlanFlag type={SemanticInformationType.WARNING} data-testid="plan-flag">
          <AccessTime size={20} color="currentColor" />
          Plan ending soon
        </PlanFlag>
      )}

      {planBanner === PlanFlagTypes.ACTIVE_TRIAL && (
        <PlanFlag type={SemanticInformationType.WARNING} data-testid="plan-flag">
          <AccessTime size={20} color="currentColor" />
          Trial ending in {daysUntilRenewal} days
        </PlanFlag>
      )}

      {planBanner === PlanFlagTypes.BACKSTAGE && (
        <PlanFlag type="primary" data-testid="plan-flag">
          Exclusive
        </PlanFlag>
      )}

      {planBanner === PlanFlagTypes.TRIAL_OFFERED && (
        <PlanFlag type="primary" data-testid="plan-flag">
          Free 7 day trial
        </PlanFlag>
      )}

      {planBanner === PlanFlagTypes.DISCOUNTED && (
        <PlanFlag type={SemanticInformationType.SUCCESS} data-testid="plan-flag">
          {plan.discount.percentage_off}% discount applied
        </PlanFlag>
      )}

      <h2 data-testid="plan-name">{plan.plan_name}</h2>

      <div className={styles.planPriceWrapper} data-testid="plan-price">
        {showDiscountedPrice ? (
          <>
            <div className={styles.pricingContentsSubscription}>
              <span>
                <span className={styles.pricingContentXLarge}>{discountedUnits}</span>.{discountedRemainder}
              </span>
              <span className={styles.pricingContentMonth}> /month</span>
            </div>
            <div className={styles.pricingContentFooter} data-testid="plan-discount-duration">
              For the first {discountDuration}
            </div>
          </>
        ) : (
          <div className={styles.pricingContentsSubscription}>
            {isBackstagePlan ? (
              <span className={styles.pricingContentFree}>No monthly fee</span>
            ) : (
              <>
                <span className={isTrialPlan ? styles.pricingStrikeThrough : undefined}>
                  <span className={styles.pricingContentXLarge}>{units}</span>.{remainder}
                </span>
                <span className={styles.pricingContentMonth}> /month</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.planHeadingInfoText} data-testid="plan-credits">
        {isBackstagePlan ? (
          'Exclusive invite-only'
        ) : (
          <>
            <StarCircle color={creditsIconColour} size={20} />
            {plan.plan_credits} Premium track credit{plan.plan_credits !== 1 ? 's' : ''}
          </>
        )}
      </div>

      <PlanFeatures features={plan.features} />

      <div className={styles.planButtonWrapper}>
        {showDiscountedPrice && isRegistration && (
          <p className={styles.postDiscountPriceText}>
            {units}.{remainder}/month after the first {discountDuration}
          </p>
        )}

        {planAction === PlanActions.UPGRADE && (
          <Button
            className={styles.planButton}
            prominence={ButtonProminence.PRIMARY}
            size={ButtonSize.LARGE}
            onClick={() => handleUpgrade(plan)}
            disabled={!!pendingSubscription || planLoadingState}
            data-test-plan-upgrade-btn={plan.plan_name}
          >
            Upgrade
          </Button>
        )}

        {planAction === PlanActions.DOWNGRADE && (
          <Button
            className={styles.planButton}
            prominence={ButtonProminence.SECONDARY}
            size={ButtonSize.LARGE}
            onClick={() => handleDowngrade(plan)}
            disabled={!!pendingSubscription || planLoadingState}
            data-test-plan-downgrade-btn={plan.plan_name}
          >
            Downgrade
          </Button>
        )}

        {planAction === PlanActions.PLAN_ACTIVE && (
          <Button
            className={styles.planButton}
            prominence={ButtonProminence.SECONDARY}
            size={ButtonSize.LARGE}
            disabled
            data-test-plan-active-btn={plan.plan_name}
          >
            Plan active
            <Checkmark size={28} color="currentColor" className={styles.planButtonIcon} />
          </Button>
        )}

        {planAction === PlanActions.RESUBSCRIBE && (
          <Button
            className={styles.planButton}
            prominence={ButtonProminence.SECONDARY}
            size={ButtonSize.LARGE}
            onClick={() => handleResumeCancelledPlan(plan)}
            data-test-plan-resubscribe-btn
            disabled={isBackstagePlan}
          >
            Re-subscribe
            <Refresh size={28} className={styles.planButtonIcon} />
          </Button>
        )}

        {planAction === PlanActions.SELECT_PLAN && (
          <ActionButton
            className={styles.planButton}
            onClick={() => handleSelectPlan(plan)}
            data-test-plan-block-action-button={plan.plan_name}
            disabled={!!pendingSubscription || isSubscribedToV2PlanAndCancelled || planLoadingState}
          >
            {isTrialPlan ? 'Start free trial' : 'Select plan'}
          </ActionButton>
        )}
      </div>

      {creditsText && (
        <p data-test-plan-card-details="credits text" className={classes(styles.smallText)}>
          {creditsText}
        </p>
      )}

      {isSubscribedToV2PlanAndCancelled ? (
        <p data-test-plan-card-details="cancelled" className={classes(styles.smallText)}>
          {!isActivePlan ? (
            'Plan unavailable while cancellation in progress'
          ) : (
            <strong className={styles.warningText}>
              Your {isSubscribedToV2TrialPlan ? 'trial' : 'plan'} is ending on{' '}
              {toDateString(currentSubscription.renewal_date)}
            </strong>
          )}
        </p>
      ) : (
        <>
          {isPendingPlan && (
            <p data-test-plan-card-details="pending" className={classes(styles.smallText)}>
              Starting on {toDateString(pendingSubscription.start_date)}
            </p>
          )}

          {isActivePlan && (
            <p data-test-plan-card-details="date" className={classes(styles.smallText)} data-testid="date">
              {pendingSubscription ? (
                <strong>Your plan will downgrade on {toDateString(pendingSubscription.start_date)}</strong>
              ) : isActiveAndDiscountedPlan ? (
                <strong className={styles.greenText}>
                  {units}.{remainder}/month from {toDateString(subscriptionDiscount.end_date)}
                </strong>
              ) : (
                <strong>
                  {isSubscribedToV2TrialPlan ? 'Plan starting' : 'Your plan will renew'} on{' '}
                  {toDateString(currentSubscription.renewal_date)}
                </strong>
              )}
            </p>
          )}

          {!!pendingSubscription && !isPendingPlan && !isActivePlan && (
            <p data-test-plan-card-details="downgrade" className={classes(styles.smallText)}>
              Plan unavailable while downgrade is in progress
            </p>
          )}

          {showMoneyBackGuarantee && !isLaunchpadPlan && <MoneyBackGuarantee />}

          {showMoneyBackGuarantee && isLaunchpadPlan && <div style={height('24px')}></div>}

          {!isSubscribedToV2Plan && isBackstagePlan && <p className={styles.smallText}>No upfront cost</p>}

          {!isSubscribedToV2Plan && isTrialPlan && <TrialPlanTooltip />}
        </>
      )}
    </PaymentPlanBlock>
  );
};
